import { useMemo, useState, type CSSProperties } from 'react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  ServiceOrderStatus,
  ServiceSeverity,
  UserRole,
  type ServiceOrderCategory,
  type CustomerSummaryDto,
  type StoreSummaryDto,
} from '@bhops/shared';
import {
  adminServiceOrdersApi,
  serviceOrdersApi,
  UNASSIGNED_SENTINEL,
  type ListServiceOrdersQuery,
} from '@/api/service-orders';
import { extractApiError } from '@/api/client';
import { Button, Field, Input, Textarea } from '@/components/Form';
import { ConfirmActionModal } from '@/components/ConfirmActionModal';
import { CustomerPicker } from '@/components/CustomerPicker';
import { StorePicker } from '@/components/StorePicker';
import { useToast } from '@/components/Toast';
import { Icon } from '@/components/Icon';
import { useAuthStore } from '@/stores/auth';
import { useUserLookup } from '@/hooks/useUserLookup';

// plan-field-service-orders.md §F-7 — focused triage queue.
//
// Daily on-call workspace: P1 + P2 in OPEN status, sorted by
// triageDeadlineAt ascending. Most-about-to-breach row is
// always at the top so the operator's eye lands on the right
// thing without scrolling.
//
// Multi-select + bulk action bar:
//   - Bulk assign to me (or to anyone via prompt)
//   - Bulk → 분류완료 (TRIAGED)
//
// Anything more (per-row notes, transitions to non-TRIAGED) is
// the broader inbox's job at /service-orders.

// BUG-31 — When viewing P1+P2 we run two parallel queries (one per severity)
// and merge client-side. With a single PAGE_SIZE of 50, a busy P1 queue that
// exceeds 50 rows would be silently truncated. Bump to 100 in combined mode
// so a typical day's P1 load isn't cut off. Single-severity views keep 50.
function getPageSize(severity: ServiceSeverity | 'P1+P2'): number {
  return severity === 'P1+P2' ? 100 : 50;
}

const SEVERITY_LABEL: Record<ServiceSeverity, string> = {
  P1: 'P1',
  P2: 'P2',
  P3: 'P3',
};

function rowIsBreached(row: {
  triageDeadlineAt: string | null;
  triagedAt: string | null;
}): boolean {
  if (!row.triageDeadlineAt || row.triagedAt) return false;
  return new Date(row.triageDeadlineAt).getTime() < Date.now();
}

// Kuun ServiceCard 등급별 색 — 시안 service-triage.jsx ServiceCard 와 동일.
function severityColor(s: ServiceSeverity): string {
  return s === ServiceSeverity.P1
    ? 'var(--severity-urgent)'
    : s === ServiceSeverity.P2
      ? 'var(--dday-warning)'
      : 'var(--report-new)';
}

const CATEGORY_LABEL: Record<ServiceOrderCategory, string> = {
  PREVENTIVE: '정기점검',
  REACTIVE: '긴급조치',
  INSTALL: '설치',
  REMOVAL: '회수',
  PARTS_SWAP: '부품교체',
};

// 분류 마감까지 남은 시간(상대) — 30분 이하/초과면 warn. 시안 SLA 박스용.
function fmtRemaining(iso: string | null | undefined): { text: string; warn: boolean } {
  if (!iso) return { text: '–', warn: false };
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return { text: '초과', warn: true };
  const mins = Math.floor(ms / 60_000);
  const warn = mins <= 30;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h >= 24) return { text: `${Math.floor(h / 24)}일 ${h % 24}시간`, warn };
  if (h > 0) return { text: `${h}시간 ${m}분`, warn };
  return { text: `${m}분`, warn };
}

// 접수 경과(상대). 시안 "접수 12분 전".
function fmtAge(iso: string | null | undefined): string {
  if (!iso) return '–';
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (mins < 1) return '방금';
  if (mins < 60) return `${mins}분 전`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}시간 전`;
  const d = Math.floor(h / 24);
  return d === 1 ? '어제' : `${d}일 전`;
}

export function ServiceOrdersTriagePage() {
  const toast = useToast();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const { resolve: resolveUser, userMap, isLoading: userMapLoading } = useUserLookup();
  const canAct =
    user?.role === UserRole.ADMIN ||
    user?.role === UserRole.MANAGER ||
    user?.role === UserRole.FIELD_MANAGER ||
    user?.role === UserRole.MFG_MANAGER ||
    user?.role === UserRole.SALES_MANAGER;

  // The triage queue is opinionated: P1+P2 OPEN, sorted by
  // triageDeadlineAt asc. Operators can toggle "내것만"
  // (assignedTo = me) or "미배정만" but the rest is locked.
  const [scope, setScope] = useState<'all' | 'unassigned' | 'mine'>('all');
  const [severity, setSeverity] = useState<ServiceSeverity | 'P1+P2'>('P1+P2');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // v0.5.x admin bulk reassign-customer — modal state lives at
  // the page level so the ConfirmActionModal can stay a pure
  // controlled component. Pickers hold full DTOs so the submit
  // path reads `.id` without a follow-up GET. The modal is
  // ADMIN-only — server enforces with @Roles(ADMIN); UI gates
  // the button visibility to match.
  const [bulkReassignModalOpen, setBulkReassignModalOpen] = useState(false);
  const [bulkReassignCustomer, setBulkReassignCustomer] = useState<CustomerSummaryDto | null>(null);
  const [bulkReassignStore, setBulkReassignStore] = useState<StoreSummaryDto | null>(null);
  const [bulkReassignReason, setBulkReassignReason] = useState('');

  // 다른 운영자에게 배정 — modal-based operator ID input (replaces window.prompt).
  const [assignOperatorModalOpen, setAssignOperatorModalOpen] = useState(false);
  const [assignOperatorId, setAssignOperatorId] = useState('');
  // Validation error shown under the input. Cleared on edit / modal close.
  // Prevents accepting arbitrary text that would FK-violate server-side.
  const [assignOperatorError, setAssignOperatorError] = useState<string | null>(null);

  const filters: ListServiceOrdersQuery = useMemo(() => {
    const f: ListServiceOrdersQuery = {
      status: ServiceOrderStatus.OPEN,
      page: 1,
      pageSize: getPageSize(severity),
      sortBy: 'triageDeadlineAt',
      sortDir: 'asc',
    };
    if (severity !== 'P1+P2') f.severity = severity;
    if (scope === 'unassigned') f.assignedToId = UNASSIGNED_SENTINEL;
    if (scope === 'mine' && user?.id) f.assignedToId = user.id;
    return f;
  }, [scope, severity, user?.id]);

  // Two queries when severity is P1+P2, one when it's a single
  // grade. We always fold them client-side and re-sort by
  // triageDeadlineAt asc so a P1 with a 30-min lead stays above
  // a P2 with a longer lead.
  const p1Q = useQuery({
    queryKey: ['service-orders-triage', { ...filters, severity: ServiceSeverity.P1 }],
    queryFn: () => serviceOrdersApi.list({ ...filters, severity: ServiceSeverity.P1 }),
    enabled: severity === 'P1+P2' || severity === ServiceSeverity.P1,
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
  const p2Q = useQuery({
    queryKey: ['service-orders-triage', { ...filters, severity: ServiceSeverity.P2 }],
    queryFn: () => serviceOrdersApi.list({ ...filters, severity: ServiceSeverity.P2 }),
    enabled: severity === 'P1+P2' || severity === ServiceSeverity.P2,
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });

  const items = useMemo(() => {
    const merged = [
      ...(severity === ServiceSeverity.P2 ? [] : (p1Q.data?.items ?? [])),
      ...(severity === ServiceSeverity.P1 ? [] : (p2Q.data?.items ?? [])),
    ];
    return merged.sort((a, b) => {
      const ad = a.triageDeadlineAt ? new Date(a.triageDeadlineAt).getTime() : Infinity;
      const bd = b.triageDeadlineAt ? new Date(b.triageDeadlineAt).getTime() : Infinity;
      return ad - bd;
    });
  }, [p1Q.data, p2Q.data, severity]);
  const total =
    severity === ServiceSeverity.P1
      ? (p1Q.data?.total ?? 0)
      : severity === ServiceSeverity.P2
        ? (p2Q.data?.total ?? 0)
        : (p1Q.data?.total ?? 0) + (p2Q.data?.total ?? 0);
  const fetching = p1Q.isFetching || p2Q.isFetching;
  const errorMsg =
    (p1Q.error && extractApiError(p1Q.error)) || (p2Q.error && extractApiError(p2Q.error)) || null;

  function invalidate(): void {
    qc.invalidateQueries({ queryKey: ['service-orders-triage'] });
    qc.invalidateQueries({ queryKey: ['service-orders'] });
    setSelected(new Set());
  }

  const bulkAssignMx = useMutation({
    mutationFn: (args: { ids: string[]; assignedToId: string | null }) =>
      serviceOrdersApi.bulkAssign({ ids: args.ids, assignedToId: args.assignedToId }),
    onSuccess: (data) => {
      const ok = data.succeeded.length;
      const fail = data.failed.length;
      if (fail === 0) {
        toast.success(`${ok}건을 배정했습니다.`);
      } else {
        toast.warn(`${ok}건은 처리했고 ${fail}건은 실패했습니다. ${data.failed[0]?.error ?? ''}`);
      }
      invalidate();
    },
    onError: (err) => toast.error(extractApiError(err), '배정 실패'),
  });

  const bulkReassignCustomerMx = useMutation({
    mutationFn: (args: {
      ids: string[];
      customerId: string;
      storeId: string | null;
      reason: string;
    }) =>
      adminServiceOrdersApi.bulkReassignCustomer({
        ids: args.ids,
        customerId: args.customerId,
        storeId: args.storeId,
        reason: args.reason,
      }),
    onSuccess: (data) => {
      const ok = data.succeeded.length;
      const fail = data.failed.length;
      if (fail === 0) {
        toast.success(`${ok}건의 고객을 다시 배정했습니다.`);
      } else {
        toast.warn(`${ok}건은 처리했고 ${fail}건은 실패했습니다. ${data.failed[0]?.error ?? ''}`);
      }
      setBulkReassignModalOpen(false);
      setBulkReassignCustomer(null);
      setBulkReassignStore(null);
      setBulkReassignReason('');
      invalidate();
    },
    onError: (err) => toast.error(extractApiError(err), '고객 재배정 실패'),
  });

  const bulkTransitionMx = useMutation({
    mutationFn: (args: { ids: string[]; to: ServiceOrderStatus }) =>
      serviceOrdersApi.bulkTransition({ ids: args.ids, to: args.to }),
    onSuccess: (data) => {
      const ok = data.succeeded.length;
      const fail = data.failed.length;
      if (fail === 0) {
        toast.success(`${ok}건의 상태를 바꿨습니다.`);
      } else {
        toast.warn(`${ok}건은 처리했고 ${fail}건은 실패했습니다. ${data.failed[0]?.error ?? ''}`);
      }
      invalidate();
    },
    onError: (err) => toast.error(extractApiError(err), '상태 변경 실패'),
  });

  // BUG-22 — After a refetch, `selected` may contain IDs that no longer exist
  // in `items` (e.g. another operator triaged them, or scope/severity changed).
  // Submitting such IDs to a bulk mutation would 100% fail server-side. Filter
  // the selection down to currently-visible items so the bulk action bar's
  // count, mutation payload, and "Select all" toggle stay consistent.
  const effectiveSelected = useMemo(() => {
    const itemIds = new Set(items.map((i) => i.id));
    return new Set([...selected].filter((id) => itemIds.has(id)));
  }, [selected, items]);

  const ids = Array.from(effectiveSelected);
  const allOnPageSelected = items.length > 0 && items.every((i) => effectiveSelected.has(i.id));

  function toggleAll(): void {
    if (allOnPageSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(items.map((i) => i.id)));
    }
  }

  function toggleOne(id: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function handleAssignToMe(): void {
    if (!user?.id || ids.length === 0) return;
    bulkAssignMx.mutate({ ids, assignedToId: user.id });
  }

  function handleAssignByPrompt(): void {
    if (ids.length === 0) return;
    setAssignOperatorId('');
    setAssignOperatorError(null);
    setAssignOperatorModalOpen(true);
  }

  function submitAssignOperator(): void {
    if (ids.length === 0) return;
    const trimmed = assignOperatorId.trim();
    // Empty = unassign (allowed). Non-empty must match a known user
    // (UUID or displayName or email); otherwise the API would
    // FK-violate or silently store bad data. The datalist surfaces
    // displayName/email as the matchable value (HTML <datalist>
    // matches against value, not inner text), so we must accept
    // id, displayName, and email forms.
    if (trimmed) {
      const byId = userMap.get(trimmed);
      const byDisplayName = !byId
        ? Array.from(userMap.values()).find((u) => u.displayName === trimmed)
        : null;
      const byEmail =
        !byId && !byDisplayName
          ? Array.from(userMap.values()).find((u) => u.email === trimmed)
          : null;
      const resolved = byId ?? byDisplayName ?? byEmail;
      if (!resolved) {
        setAssignOperatorError('입력한 이름, 이메일, ID와 맞는 운영자가 없습니다.');
        return;
      }
      bulkAssignMx.mutate({ ids, assignedToId: resolved.id });
    } else {
      bulkAssignMx.mutate({ ids, assignedToId: null });
    }
    setAssignOperatorModalOpen(false);
    setAssignOperatorId('');
    setAssignOperatorError(null);
  }

  function handleBulkTriaged(): void {
    if (ids.length === 0) return;
    bulkTransitionMx.mutate({ ids, to: ServiceOrderStatus.TRIAGED });
  }

  const isAdmin = user?.role === UserRole.ADMIN;

  // ADMIN-only bulk customer reassignment. The button just opens
  // the modal — actual mutation dispatch lives in submit handler
  // below so the modal stays a pure controlled component.
  function handleBulkReassignCustomer(): void {
    if (!isAdmin || ids.length === 0) return;
    setBulkReassignCustomer(null);
    setBulkReassignStore(null);
    setBulkReassignReason('');
    setBulkReassignModalOpen(true);
  }

  function submitBulkReassignCustomer(): void {
    if (!isAdmin || ids.length === 0) return;
    const reason = bulkReassignReason.trim();
    if (!bulkReassignCustomer || !reason) return;
    bulkReassignCustomerMx.mutate({
      ids,
      customerId: bulkReassignCustomer.id,
      storeId: bulkReassignStore?.id ?? null,
      reason,
    });
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, height: '100%' }}>
      {/* Kuun ServiceTriageAfter — 심각도 요약(클릭해 등급 필터, 다시 클릭 시 P1+P2). */}
      <div style={{ display: 'flex', gap: 14 }}>
        <SevSummaryCard
          label="P1 · 긴급"
          count={p1Q.data?.total ?? 0}
          color="var(--severity-urgent)"
          sla="분류 SLA 30분, 가장 급한 건부터"
          active={severity === ServiceSeverity.P1}
          onClick={() => {
            setSeverity(severity === ServiceSeverity.P1 ? 'P1+P2' : ServiceSeverity.P1);
            setSelected(new Set());
          }}
        />
        <SevSummaryCard
          label="P2 · 높음"
          count={p2Q.data?.total ?? 0}
          color="var(--dday-warning)"
          sla="분류 SLA 2시간"
          active={severity === ServiceSeverity.P2}
          onClick={() => {
            setSeverity(severity === ServiceSeverity.P2 ? 'P1+P2' : ServiceSeverity.P2);
            setSelected(new Set());
          }}
        />
      </div>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <h1 className="kuun-h2" style={{ margin: 0 }}>
          서비스 오더 분류 큐
        </h1>
        <select
          value={scope}
          onChange={(e) => {
            setScope(e.target.value as 'all' | 'unassigned' | 'mine');
            setSelected(new Set());
          }}
          style={selectStyle}
        >
          <option value="all">전체</option>
          <option value="unassigned">미배정만</option>
          <option value="mine">내 것만</option>
        </select>
        <Link
          to="/service-orders"
          style={{ marginLeft: 8, fontSize: 12, color: 'var(--fg-brand)' }}
        >
          전체 인박스 →
        </Link>
        <div
          className="kuun-caption"
          style={{
            marginLeft: 'auto',
            color: fetching ? 'var(--brand)' : 'var(--fg-muted)',
          }}
        >
          {fetching ? '불러오는 중…' : `분류 대기 ${total}건`}
          {severity !== 'P1+P2' ? `, ${severity}만 보는 중` : ''}
        </div>
      </div>

      {/* Bulk action bar — sticky just under the header when any rows are selected. */}
      {canAct && ids.length > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 12px',
            background: 'var(--bg-selected)',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--radius-sm)',
          }}
        >
          <span className="kuun-label">{ids.length}건 선택됨</span>
          <div style={{ flex: 1 }} />
          <Button
            type="button"
            variant="secondary"
            disabled={bulkAssignMx.isPending}
            onClick={handleAssignToMe}
          >
            나에게 배정
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={bulkAssignMx.isPending}
            onClick={handleAssignByPrompt}
          >
            다른 운영자에게 배정
          </Button>
          <Button type="button" disabled={bulkTransitionMx.isPending} onClick={handleBulkTriaged}>
            분류 완료 처리
          </Button>
          {isAdmin && (
            <Button
              type="button"
              variant="secondary"
              disabled={bulkReassignCustomerMx.isPending}
              onClick={handleBulkReassignCustomer}
              data-testid="bulk-reassign-customer-button"
            >
              고객 재배정
            </Button>
          )}
          <Button type="button" variant="secondary" onClick={() => setSelected(new Set())}>
            선택 해제
          </Button>
        </div>
      )}

      {/* 다른 운영자에게 배정 — operator ID input modal (replaces window.prompt).
          Input is validated against useUserLookup before dispatch to prevent
          FK violations / silent bad data. A datalist gives autocomplete
          across known users (id, email, displayName). */}
      <ConfirmActionModal
        open={assignOperatorModalOpen}
        onClose={() => {
          if (bulkAssignMx.isPending) return;
          setAssignOperatorModalOpen(false);
          setAssignOperatorId('');
          setAssignOperatorError(null);
        }}
        title={`${ids.length}건을 다른 운영자에게 배정`}
        submitLabel={userMapLoading ? '운영자 목록을 불러오는 중…' : '배정'}
        // Disable submit while the user lookup is still fetching —
        // otherwise the resolution (id/displayName/email) would
        // false-negative against an empty userMap.
        submitDisabled={userMapLoading}
        isSubmitting={bulkAssignMx.isPending}
        onSubmit={submitAssignOperator}
        context={
          <div>
            선택된 <strong>{ids.length}</strong>건이 입력한 운영자에게 배정됩니다.
            <br />
            비워두고 제출하면 미배정 상태로 처리됩니다.
          </div>
        }
      >
        <Field label="운영자 이름, 이메일 또는 ID" hint="비워 두면 배정이 풀립니다.">
          {/* BUG-04 fix: HTML <datalist> matches autocompletion against
              `value`, not the inner text. Previously `value={u.id}` made
              the UUID the matchable string, so typing a Korean name
              wouldn't filter the dropdown. Now `value` carries the
              displayName/email (operator-friendly) and the secondary
              text shows the email + truncated id for disambiguation.
              submitAssignOperator resolves the input back to id via
              id → displayName → email. */}
          <Input
            data-testid="assign-operator-id-input"
            list="assign-operator-list"
            value={assignOperatorId}
            onChange={(e) => {
              setAssignOperatorId(e.target.value);
              if (assignOperatorError) setAssignOperatorError(null);
            }}
            placeholder="이름, 이메일, ID로 검색"
            autoFocus
          />
          <datalist id="assign-operator-list">
            {Array.from(userMap.values()).map((u) => (
              <option key={u.id} value={u.displayName ?? u.email}>
                {u.email} ({u.id.slice(0, 8)}…)
              </option>
            ))}
          </datalist>
          {assignOperatorError && (
            <div
              style={{
                color: 'var(--fg-danger)',
                fontSize: 12,
                marginTop: 4,
              }}
            >
              {assignOperatorError}
            </div>
          )}
        </Field>
      </ConfirmActionModal>

      {/* Admin bulk reassign-customer modal — only mounted when
          there's a non-empty selection (ids drives the title). */}
      {isAdmin && (
        <ConfirmActionModal
          open={bulkReassignModalOpen}
          onClose={() => {
            if (bulkReassignCustomerMx.isPending) return;
            setBulkReassignModalOpen(false);
            setBulkReassignCustomer(null);
            setBulkReassignStore(null);
            setBulkReassignReason('');
          }}
          title={`${ids.length}건 고객 재배정`}
          submitLabel="재배정"
          submitDisabled={bulkReassignCustomer === null || bulkReassignReason.trim().length === 0}
          isSubmitting={bulkReassignCustomerMx.isPending}
          onSubmit={submitBulkReassignCustomer}
          context={
            <div>
              <div>
                선택된 <strong>{ids.length}</strong>건의 서비스 오더가 같은 새 고객과 매장으로 다시
                배정됩니다.
              </div>
              <div>이미 끝난 오더처럼 바꿀 수 없는 건은 실패로 남고, 결과 알림에 표시됩니다.</div>
            </div>
          }
        >
          <Field label="새 고객" required>
            <CustomerPicker
              value={bulkReassignCustomer}
              onChange={(c) => {
                setBulkReassignCustomer(c);
                // Same FK-safety pattern as the single-row reassign:
                // changing the customer invalidates any previously
                // picked store. Clear it so the bulk submit can't
                // send a stale store id.
                if (c?.id !== bulkReassignCustomer?.id) {
                  setBulkReassignStore(null);
                }
              }}
              data-testid="bulk-reassign-customer-picker"
            />
          </Field>
          <Field
            label="새 매장 (선택)"
            hint={
              bulkReassignCustomer
                ? '비워 두면 선택한 오더의 매장 연결이 모두 풀립니다.'
                : '고객을 먼저 선택해 주세요.'
            }
          >
            <StorePicker
              value={bulkReassignStore}
              onChange={setBulkReassignStore}
              customerId={bulkReassignCustomer?.id ?? null}
              disabled={bulkReassignCustomer === null}
              data-testid="bulk-reassign-store-picker"
            />
          </Field>
          <Field label="재배정 사유" required>
            <Textarea
              data-testid="bulk-reassign-reason-input"
              value={bulkReassignReason}
              onChange={(e) => setBulkReassignReason(e.target.value)}
              placeholder="예: 고객사 합병으로 인한 일괄 재배정"
              rows={3}
            />
          </Field>
        </ConfirmActionModal>
      )}

      {/* List */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        {fetching && items.length === 0 ? (
          <div style={emptyCard}>
            <span className="kuun-caption">불러오는 중…</span>
          </div>
        ) : errorMsg ? (
          <div style={emptyCard}>
            <span style={{ color: 'var(--fg-danger)' }}>{errorMsg}</span>
          </div>
        ) : items.length === 0 ? (
          <div style={emptyCard} className="kuun-dot-grid">
            <div className="kuun-h4">분류 대기 중인 서비스 오더가 없습니다</div>
            <div className="kuun-caption" style={{ marginTop: 4 }}>
              새 서비스 오더가 들어오면 바로 여기에 표시됩니다.
            </div>
          </div>
        ) : (
          <>
            {/* 전체 선택 헤더 행 (옛 table thead 의 "모두 선택" 보존) */}
            <div style={listHeaderRow}>
              <input
                type="checkbox"
                aria-label="모두 선택"
                checked={allOnPageSelected}
                onChange={toggleAll}
              />
              <span className="kuun-caption">전체 선택</span>
              <span className="kuun-caption" style={{ marginLeft: 'auto' }}>
                {items.length}건 표시
              </span>
            </div>
            {items.map((r) => (
              <TriageServiceCard
                key={r.id}
                row={r}
                selected={selected.has(r.id)}
                breached={rowIsBreached(r)}
                assignee={r.assignedToId ? resolveUser(r.assignedToId) : null}
                assigneeId={r.assignedToId}
                onToggle={() => toggleOne(r.id)}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
}

// Kuun ServiceTriageAfter — 풀-컨텍스트 서비스 카드(시안 service-triage.jsx ServiceCard).
// flex 가로: ① 좌측 5px 등급 컬러바(P1 펄스) ② 70px 등급 박스(체크박스+등급+카테고리)
// ③ 본문(ID mono deep-link + 상태 + 설명) ④ 110px SLA 박스(분류 마감 잔여 mono + 접수 경과)
// ⑤ 담당자 칩/미배정. 셀렉션·deep-link·aria 는 옛 table 과 동일하게 보존.
function TriageServiceCard({
  row,
  selected,
  breached,
  assignee,
  assigneeId,
  onToggle,
}: {
  row: {
    id: string;
    humanId: string;
    severity: ServiceSeverity;
    category: ServiceOrderCategory;
    description: string;
    triageDeadlineAt: string | null;
    createdAt: string;
  };
  selected: boolean;
  breached: boolean;
  assignee: string | null;
  assigneeId: string | null;
  onToggle: () => void;
}) {
  const sevColor = severityColor(row.severity);
  const isP1 = row.severity === ServiceSeverity.P1;
  const remaining = fmtRemaining(row.triageDeadlineAt);
  const slaWarn = breached || remaining.warn;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'stretch',
        background: 'var(--bg-surface)',
        border: selected ? '1px solid var(--brand)' : '1px solid var(--border-default)',
        borderRadius: 'var(--r-card)',
        overflow: 'hidden',
        boxShadow: 'var(--shadow-sm)',
      }}
    >
      {/* ① 좌측 등급 컬러바 — P1 펄스 */}
      <div
        className={isP1 ? 'kuun-urgent' : undefined}
        style={{ width: 5, background: sevColor, flexShrink: 0 }}
        aria-hidden
      />
      {/* ② 등급 박스(체크박스 + 등급 + 카테고리) */}
      <div
        style={{
          width: 70,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 4,
          padding: '10px 0',
          borderRight: '1px solid var(--border-subtle)',
          background: selected ? 'var(--bg-selected)' : undefined,
        }}
      >
        <input
          type="checkbox"
          aria-label={`${row.humanId} 선택`}
          checked={selected}
          onChange={onToggle}
        />
        <span style={{ fontSize: 20, fontWeight: 800, color: sevColor, lineHeight: 1 }}>
          {SEVERITY_LABEL[row.severity]}
        </span>
        <span style={{ fontSize: 11, color: 'var(--fg-subtle)' }}>
          {CATEGORY_LABEL[row.category]}
        </span>
      </div>
      {/* ③ 본문 */}
      <div style={{ flex: 1, minWidth: 0, padding: '13px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
          {/* deep-link: Inbox 가 location.state.openId 흡수해 자동 선택 */}
          <Link
            to="/service-orders"
            state={{ openId: row.id }}
            title={row.id}
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 12.5,
              color: 'var(--fg-brand)',
              fontWeight: 600,
            }}
          >
            {row.humanId}
          </Link>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              fontSize: 12,
              color: 'var(--report-new)',
              fontWeight: 600,
            }}
          >
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                background: 'var(--report-new)',
              }}
              aria-hidden
            />
            분류 대기
          </span>
          {breached && (
            <span
              title="분류 마감 초과"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                padding: '1px 8px',
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 700,
                background: 'var(--bg-danger-subtle)',
                color: 'var(--fg-danger)',
              }}
            >
              <Icon name="clock" size={12} color="var(--fg-danger)" aria-hidden />
              SLA 초과
            </span>
          )}
        </div>
        <div
          style={{
            fontSize: 15,
            fontWeight: 600,
            marginTop: 7,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {row.description}
        </div>
      </div>
      {/* ④ SLA 박스 */}
      <div
        style={{
          width: 116,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '0 14px',
          borderLeft: '1px solid var(--border-subtle)',
        }}
      >
        <span style={{ fontSize: 11.5, color: 'var(--fg-subtle)' }}>분류 마감</span>
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 18,
            fontWeight: 700,
            color: slaWarn ? 'var(--severity-urgent)' : 'var(--fg-default)',
            marginTop: 2,
          }}
        >
          {remaining.text}
        </span>
        <span style={{ fontSize: 11.5, color: 'var(--fg-subtle)', marginTop: 2 }}>
          접수 {fmtAge(row.createdAt)}
        </span>
      </div>
      {/* ⑤ 담당자 */}
      <div
        style={{
          width: 132,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '0 16px',
          borderLeft: '1px solid var(--border-subtle)',
        }}
      >
        {assignee ? (
          <>
            <span
              style={{
                width: 26,
                height: 26,
                borderRadius: '50%',
                background: 'var(--brand)',
                color: 'var(--brand-on)',
                fontSize: 11,
                fontWeight: 700,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
              aria-hidden
            >
              {assignee.slice(0, 1)}
            </span>
            <span
              title={assigneeId ?? undefined}
              style={{
                fontSize: 13,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {assignee}
            </span>
          </>
        ) : (
          <span style={{ fontSize: 13, color: 'var(--dday-warning)', fontWeight: 600 }}>
            미배정
          </span>
        )}
      </div>
    </div>
  );
}

// Kuun ServiceTriageAfter — 심각도 요약 카드. 좌측 5px 컬러바 + 큰 카운트(상태색)
// + 라벨 + SLA 안내. 클릭 시 등급 필터 토글(부모의 setSeverity 와이어링).
function SevSummaryCard({
  label,
  count,
  color,
  sla,
  active,
  onClick,
}: {
  label: string;
  count: number;
  color: string;
  sla: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        flex: 1,
        display: 'flex',
        textAlign: 'left',
        padding: 0,
        background: 'var(--bg-surface)',
        border: active ? `1px solid ${color}` : '1px solid var(--border-default)',
        borderRadius: 'var(--r-card)',
        boxShadow: 'var(--shadow-md)',
        overflow: 'hidden',
        cursor: 'pointer',
        fontFamily: 'var(--font-sans)',
      }}
    >
      <span style={{ width: 5, background: color, flexShrink: 0 }} aria-hidden />
      <span style={{ padding: '14px 16px', flex: 1 }}>
        <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span
            style={{
              fontSize: 32,
              fontWeight: 800,
              lineHeight: 1,
              color,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {count}
          </span>
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--fg-default)' }}>{label}</span>
        </span>
        <span style={{ display: 'block', fontSize: 12.5, color: 'var(--fg-muted)', marginTop: 7 }}>
          {sla}
        </span>
      </span>
    </button>
  );
}

const listHeaderRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '6px 14px',
  color: 'var(--fg-muted)',
};
const emptyCard: CSSProperties = {
  flex: 1,
  minHeight: 320,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--fg-muted)',
  padding: 32,
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  background: 'var(--bg-surface)',
  boxShadow: 'var(--shadow-sm)',
};
const selectStyle: CSSProperties = {
  height: 32,
  padding: '0 10px',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-surface)',
  fontSize: 13,
};
